(() => {
  'use strict';

  // Shopify may evaluate section assets again after an editor reload.
  if (window.pfmHolidayMatch) {
    window.pfmHolidayMatch.init(document);
    return;
  }

  const selector = '.pfm-holiday[data-holiday-quiz]';
  const instances = new WeakMap();

  class HolidayMatch {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-quiz-controls]');
      this.steps = {
        recipient: root.querySelector('[data-quiz-step="recipient"]'),
        need: root.querySelector('[data-quiz-step="need"]')
      };
      this.results = root.querySelector('[data-quiz-results]');
      this.cards = Array.from(root.querySelectorAll('[data-recommendation]'));
      this.missing = root.querySelector('[data-quiz-missing]');
      this.progress = root.querySelector('[data-quiz-progress]');
      this.progressBar = root.querySelector('[data-quiz-progress-bar]');
      this.resultHeader = root.querySelector('[data-quiz-result-header]');
      this.resultActions = root.querySelector('[data-quiz-result-actions]');
      this.fallbackHeading = root.querySelector('[data-quiz-fallback-heading]');
      this.answers = { recipient: '', need: '' };
      this.started = false;
      this.stage = 'recipient';
      this.onSubmit = this.onSubmit.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      if (!this.form || !this.steps.recipient || !this.steps.need || !this.results) return;

      this.form.reset();
      this.form.addEventListener('submit', this.onSubmit);
      this.form.addEventListener('change', this.onChange);
      this.root.addEventListener('click', this.onClick);
      this.root.addEventListener('shopify:block:select', this.onBlockSelect);
      this.root.querySelectorAll('[data-quiz-placeholder]').forEach((item) => { item.hidden = true; });
      this.form.hidden = false;
      this.fallbackHeading.hidden = true;
      this.showStage('recipient', false);
      this.root.dataset.quizReady = 'true';
    }

    emit(event, card) {
      const detail = { event };
      if (this.answers.recipient) detail.recipient = this.answers.recipient;
      if (this.answers.need) detail.need = this.answers.need;
      if (card && card.dataset.productHandle) detail.product_handle = card.dataset.productHandle;
      if (card && card.dataset.collectionHandle) detail.collection_handle = card.dataset.collectionHandle;
      this.root.dispatchEvent(new CustomEvent('pfm:holiday:interaction', { bubbles: true, detail }));
    }

    start() {
      if (this.started) return;
      this.started = true;
      this.emit('quiz_start');
    }

    onChange(event) {
      if (event.target.type !== 'radio') return;
      const step = event.target.closest('[data-quiz-step]');
      if (!step || !Object.prototype.hasOwnProperty.call(this.answers, step.dataset.quizStep)) return;
      this.answers[step.dataset.quizStep] = event.target.value;
      this.start();
    }

    onSubmit(event) {
      event.preventDefault();
      const currentStep = this.steps[this.stage];
      if (!currentStep) return;
      const selected = currentStep.querySelector('input:checked');
      if (!selected) {
        const first = currentStep.querySelector('input');
        if (first) first.reportValidity();
        return;
      }

      this.answers[this.stage] = selected.value;
      this.start();
      if (this.stage === 'recipient') {
        this.showStage('need');
        return;
      }

      const match = this.findMatch();
      this.showResult(match);
      this.emit('quiz_complete', match);
    }

    findMatch() {
      const matches = this.cards.filter((card) => card.dataset.need === this.answers.need);
      return matches.find((card) => card.dataset.recipient === this.answers.recipient)
        || matches.find((card) => card.dataset.recipient === 'any')
        || null;
    }

    onClick(event) {
      const back = event.target.closest('[data-quiz-back]');
      if (back && this.root.contains(back)) {
        this.showStage(this.stage === 'result' ? 'need' : 'recipient');
        return;
      }
      const reset = event.target.closest('[data-quiz-reset]');
      if (reset && this.root.contains(reset)) {
        this.form.reset();
        this.answers = { recipient: '', need: '' };
        this.started = false;
        this.showStage('recipient');
        return;
      }
      const link = event.target.closest('a');
      const card = link && link.closest('[data-recommendation]');
      if (card && this.root.contains(card)) this.emit('recommendation_click', card);
    }

    showStage(stage, moveFocus = true) {
      this.stage = stage;
      this.form.hidden = false;
      this.results.hidden = true;
      this.results.classList.remove('is-match');
      Object.entries(this.steps).forEach(([name, step]) => {
        step.hidden = name !== stage;
        step.disabled = name !== stage;
      });
      this.progress.textContent = stage === 'recipient' ? 'Question 1 of 2' : 'Question 2 of 2';
      this.progressBar.style.width = stage === 'recipient' ? '50%' : '100%';
      if (moveFocus) {
        const input = this.steps[stage].querySelector('input:checked') || this.steps[stage].querySelector('input');
        if (input) input.focus({ preventScroll: true });
      }
    }

    showResult(card, moveFocus = true) {
      this.stage = 'result';
      this.form.hidden = true;
      this.results.hidden = false;
      this.results.classList.add('is-match');
      this.resultHeader.hidden = false;
      this.resultActions.hidden = false;
      this.fallbackHeading.hidden = true;
      Object.values(this.steps).forEach((step) => { step.disabled = true; });
      this.cards.forEach((item) => { item.hidden = item !== card; });
      this.missing.hidden = Boolean(card);
      if (moveFocus) this.root.querySelector('[data-quiz-result-heading]').focus({ preventScroll: true });
    }

    onBlockSelect(event) {
      const card = event.target.closest('[data-recommendation]');
      if (card && this.root.contains(card)) this.showResult(card, false);
    }

    destroy() {
      this.form.removeEventListener('submit', this.onSubmit);
      this.form.removeEventListener('change', this.onChange);
      this.root.removeEventListener('click', this.onClick);
      this.root.removeEventListener('shopify:block:select', this.onBlockSelect);
      delete this.root.dataset.quizReady;
    }
  }

  function rootsIn(scope) {
    const roots = Array.from(scope.querySelectorAll(selector));
    if (scope.matches && scope.matches(selector)) roots.unshift(scope);
    return roots;
  }

  function init(scope) {
    rootsIn(scope).forEach((root) => {
      if (!instances.has(root)) instances.set(root, new HolidayMatch(root));
    });
  }

  window.pfmHolidayMatch = { init };
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    rootsIn(event.target).forEach((root) => {
      const instance = instances.get(root);
      if (instance) instance.destroy();
      instances.delete(root);
    });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
})();
